import { CarouselProps } from '@/components/blocks/ProductDetails/Carousel';
import CloseIcon from '@mui/icons-material/Close';
import { Box, ButtonBase, Modal, Stack, useMediaQuery, useTheme, Fade } from '@mui/material';
import { ProductDetailsSingleCarousel } from '@/components/blocks/ProductDetails/ProductDetailsSingleCarousel';
import {
	buttonCarouselModalFrameSX,
	closeButtonCarouselModalFrameSX,
	containerCarouselModalFrameSX,
	contentCarouselModalFrameSX,
	footerCarouselModalFrameSX,
	headerCarouselModalFrameSX,
	iconCarouselModalFrameSX,
	modalCarouselModalFrameSX,
	sliderImageFrameCarouselModalFrameSX,
} from '@/components/blocks/ProductDetails/styles/carrouselModalFrame';
import { useEffect, useRef, useState } from 'react';
import { CounterSlides } from '@/components/content/ProductDetails/parts/CounterSlides';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import type { Swiper as SwiperType } from 'swiper';

type Props = CarouselProps & { open: boolean; onClose: () => void };

const MD_UP_DIMS = { slideWidth: 12, slideHeight: 15, slidesToShow: 8 };
const SM_DIMS = { slideWidth: 5, slideHeight: 3, slidesToShow: 1 };
const XS_DIMS = { slideWidth: 1, slideHeight: 1, slidesToShow: 1 };

export const ProductDetailsGalleryModal: React.FC<Props> = ({ open, onClose, ...props }) => {
	const [indexImage, setIndexImage] = useState(0);

	const theme = useTheme();
	const isSm = useMediaQuery(theme.breakpoints.down('md'));
	const isXs = useMediaQuery(theme.breakpoints.down('sm'));

	const { slidesToShow } = isXs ? XS_DIMS : isSm ? SM_DIMS : MD_UP_DIMS;

	const slides = props.slides.map(({ thumbnail, name, fullImage }) => {
		const src = isXs ? fullImage : isSm ? fullImage : thumbnail;
		return { src, alt: name };
	});

	const swiperRef = useRef<SwiperType | null>(null);

	const prevSlide = () => {
		swiperRef.current?.slidePrev();
	};
	const nextSlide = () => {
		swiperRef.current?.slideNext();
	};

	const initialIndex = props.index ?? 0;

	useEffect(() => {
		if (open) {
			setIndexImage(initialIndex);
		} else {
			swiperRef.current = null;
		}
	}, [open, initialIndex]);

	const handleClose = () => {
		props.choose(indexImage);
		onClose();
	};

	const disableNextSlide = indexImage === props.slides.length - 1;
	const disablePrevSlide = indexImage === 0;

	return (
		<Modal open={open} sx={modalCarouselModalFrameSX} disableScrollLock onClose={handleClose}>
			<Fade in={open} timeout={500}>
				<Box sx={containerCarouselModalFrameSX}>
					<Box sx={contentCarouselModalFrameSX}>
						<Box sx={headerCarouselModalFrameSX}>
							<CloseIcon onClick={handleClose} sx={closeButtonCarouselModalFrameSX} />
						</Box>
						<ProductDetailsSingleCarousel
							slides={slides}
							slidesToShow={slidesToShow}
							initialSlide={initialIndex}
							imageFrameSX={sliderImageFrameCarouselModalFrameSX}
							onSwiper={(swiper) => {
								swiperRef.current = swiper;
							}}
							onSlideChange={setIndexImage}
						/>
						<Box sx={footerCarouselModalFrameSX}>
							<CounterSlides
								selectedNumber={indexImage + 1}
								totalSlides={props.slides.length}
								isCarouselModalFrame
							/>

							<Stack direction="row" spacing={2}>
								<ButtonBase
									sx={buttonCarouselModalFrameSX(disablePrevSlide)}
									disabled={disablePrevSlide}
									onClick={prevSlide}
								>
									<ArrowBackIosNewIcon sx={iconCarouselModalFrameSX(disablePrevSlide)} />
								</ButtonBase>
								<ButtonBase
									sx={buttonCarouselModalFrameSX(disableNextSlide)}
									disabled={disableNextSlide}
									onClick={nextSlide}
								>
									<ArrowForwardIosIcon sx={iconCarouselModalFrameSX(disableNextSlide)} />
								</ButtonBase>
							</Stack>
						</Box>
					</Box>
				</Box>
			</Fade>
		</Modal>
	);
};
